import { CloseRoundLight, DoneLight, SettingLineLight, SlantPilotCustom, TableLight } from '@repo/go-icons';
import { ActionsBar, Button, ConfigContext, Switch, Tooltip } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { DoubleTooltipSidebarItem } from '../../../shared/components/DoubleTooltipSidebarItem/DoubleTooltipSidebarItem';
import { SelectableWellboreList } from '../../../shared/components/SelectableWellboreList/SelectableWellboreList';
import { TimezoneSwitch } from '../../../shared/components/TimezoneSwitch/TimezoneSwitch';
import type { AlertsManagerWidgetState } from '../../AlertsManagerWidgetState';
import type { AlertRulesTableStore } from '../AlertRulesTableStore';
import type { AlertRulesService } from '../services/AlertRulesService';

export const AlertRulesActionsBar = observer(function AlertRulesActionsBar({
  service,
  widgetState,
  store,
}: {
  widgetState: AlertsManagerWidgetState;
  service: AlertRulesService;
  store: AlertRulesTableStore;
}) {
  const { t, i18n } = useTranslation();
  const configProvider = useContext(ConfigContext);
  const editing = service.uiState.editing;
  const save = () => {
    service
      .saveAlertRules()
      .then(() => {
        service.setEditing(false);
        store.setIsContextPanelOpen(false);
        widgetState.eventHandlers.onEditModeChanged?.(false);
      })
      .catch((error) => {
        console.error(error);
      });
  };
  const cancel = () => {
    widgetState.exitEditModeAsync(configProvider, i18n).catch((error) => {
      console.error(error);
    });
  };
  return (
    <ActionsBar>
      <ActionsBar.TopBar>
        <DoubleTooltipSidebarItem
          outerTitle={t('alertsManagerWidget:alertRules.actionsBar.wellboreSelection')}
          innerTitle={() => (
            <SelectableWellboreList
              wellbores={service.wellbores.list}
              onChange={(id) => service.setWellboreById(id)}
              value={service.wellbores.selected?.id}
              activeTitle={t('alertsManagerWidget:alertRules.actionsBar.activeWellbore')}
            />
          )}
          icon={<SlantPilotCustom />}
        />

        <Tooltip placement="left" title={t('alertsManagerWidget:alertRules.actionsBar.template')} delay={400}>
          <ActionsBar.Item
            active={service.isTemplatesDrawerOpen}
            onClick={() => service.setIsTemplatesDrawerOpen(!service.isTemplatesDrawerOpen)}
          >
            <TableLight />
          </ActionsBar.Item>
        </Tooltip>

        {editing && (
          <>
            <ActionsBar.Space>
              <Tooltip placement="left" title={t('alertsManagerWidget:actionsBar.save')} delay={400}>
                <div>
                  <Button icon={<DoneLight />} variant="success" onClick={() => save()} />
                </div>
              </Tooltip>
            </ActionsBar.Space>

            <ActionsBar.Space>
              <Tooltip placement="left" title={t('alertsManagerWidget:actionsBar.cancelChanges')} delay={400}>
                <div>
                  <Button icon={<CloseRoundLight />} variant="danger" onClick={() => cancel()} />
                </div>
              </Tooltip>
            </ActionsBar.Space>
          </>
        )}
      </ActionsBar.TopBar>
      <ActionsBar.BottomBar>
        <Tooltip placement="left" title={t('alertsManagerWidget:tableSettings')} delay={400}>
          <ActionsBar.Item active={store.isTableSettingsOpen} onClick={() => store.setIsTableSettingsOpen(true)}>
            <SettingLineLight />
          </ActionsBar.Item>
        </Tooltip>
        <TimezoneSwitch timeZones={widgetState.timeZonesStore} />
        <Switch
          style={{ marginLeft: 4 }}
          orientation="vertical"
          size="m"
          checked={widgetState.settingsStore.scaleDepthUnit === 'ft'}
          leftNode={'m'}
          rightNode={'ft'}
          onChange={() =>
            widgetState.settingsStore.setScaleDepthUnit(widgetState.settingsStore.scaleDepthUnit === 'm' ? 'ft' : 'm')
          }
        />
      </ActionsBar.BottomBar>
    </ActionsBar>
  );
});
